import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { BUCKET_LABELS, BUCKET_ORDER, type CorpusBucket, type CorpusFolder } from "@/lib/workspaceCorpus";

type FolderDialogState = {
  open: boolean;
  bucket: CorpusBucket;
  parentId: string | null;
  parentName?: string;
};

type CreateFolderDialogProps = {
  state: FolderDialogState;
  onOpenChange: (open: boolean) => void;
  name: string;
  onNameChange: (name: string) => void;
  onBucketChange: (bucket: CorpusBucket) => void;
  onSubmit: () => void;
};

export const CreateCorpusFolderDialog = ({
  state,
  onOpenChange,
  name,
  onNameChange,
  onBucketChange,
  onSubmit,
}: CreateFolderDialogProps) => (
  <Dialog open={state.open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {state.parentId ? "New sub-folder" : `New ${BUCKET_LABELS[state.bucket]} folder`}
        </DialogTitle>
        {state.parentName && (
          <DialogDescription>Inside “{state.parentName}”.</DialogDescription>
        )}
      </DialogHeader>
      <div className="space-y-2">
        <label className="text-xs font-medium">Name</label>
        <Input
          autoFocus
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Manifesto, Voice samples"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
        {!state.parentId && (
          <>
            <label className="text-xs font-medium">Bucket</label>
            <Select value={state.bucket} onValueChange={(v) => onBucketChange(v as CorpusBucket)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUCKET_ORDER.map((b) => (
                  <SelectItem key={b} value={b}>
                    {BUCKET_LABELS[b]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit}>Create</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

type RenameFolderDialogProps = {
  open: boolean;
  folder: CorpusFolder | null;
  value: string;
  onValueChange: (v: string) => void;
  onClose: () => void;
  onSubmit: () => void;
};

export const RenameCorpusFolderDialog = ({
  open,
  folder: _folder,
  value,
  onValueChange,
  onClose,
  onSubmit,
}: RenameFolderDialogProps) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Rename folder</DialogTitle>
      </DialogHeader>
      <Input
        autoFocus
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
        }}
      />
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit}>Save</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export type { FolderDialogState };
